import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarIcon, Clock, MapPin, Plus, X, ArrowLeft, Users, FileText, Sparkles, Home } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useEvents } from '@/hooks/useEvents';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { eventSchema, guestSchema, optionSchema } from '@/lib/validation';
import { securityUtils } from '@/lib/securityUtils';
import { Event, EventOption } from '@/types/event';
import { validateDatabaseAuth, debugAuthState } from '@/lib/authUtils';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { EventTemplates } from './EventTemplates';
import { BulkGuestManager } from './BulkGuestManager';
import { GuestContactSelector } from './GuestContactSelector';
import { FamilyMemberSelector } from './FamilyMemberSelector';
import ClientSelector from './ClientSelector';

interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  created_at: string;
  updated_at: string;
}

const formSchema = z.object({
  name: z.string().min(1, 'Event name is required'),
  description: z.string().optional(),
  hostName: z.string().min(1, 'Host name is required'),
  deadline: z.date().optional(),
  eventType: z.enum(['voting', 'rsvp']).default('voting'),
  rsvpDate: z.string().optional(),
  rsvpTime: z.string().optional(),
  rsvpLocation: z.string().optional(),
  allowGuestSuggestions: z.boolean().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface CreateEventFormProps {
  onEventCreated: (event: Event) => void;
  onBack: () => void;
  templateData?: {
    name: string;
    description?: string;
    defaultDescription?: string;
    options: any[];
    category?: string;
    suggestedGuests?: string[];
  };
  familyGroupId?: string;
  familyGroupName?: string;
}

interface Guest {
  email?: string;
  phone?: string;
  name?: string;
}

export const CreateEventForm: React.FC<CreateEventFormProps> = ({ onEventCreated, onBack, templateData, familyGroupId, familyGroupName }) => {
  const [currentStep, setCurrentStep] = useState<'template' | 'details' | 'options' | 'guests'>('template');
  const [options, setOptions] = useState<EventOption[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [newOption, setNewOption] = useState<{ type: 'date' | 'time' | 'location'; value: string }>({ type: 'date', value: '' });
  const [newGuest, setNewGuest] = useState<Guest>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [formInitialized, setFormInitialized] = useState(false);
  
  const { createEvent } = useEvents();
  const { user, session, validateSession, refreshSession } = useAuth();
  const { toast } = useToast();
  const { subscription, checkCanCreateEvent, checkCanAddGuests } = useSubscription();

  // State for form initialization
  const [isFormReady, setIsFormReady] = useState(false);
  const [formDefaults, setFormDefaults] = useState<Partial<FormData>>({});

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: formDefaults,
  });

  // Fetch user profile and initialize form defaults
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id) {
        const defaults = {
          name: '',
          description: '',
          hostName: '',
          deadline: undefined,
          eventType: 'voting' as const,
          rsvpDate: '',
          rsvpTime: '',
          rsvpLocation: '',
        };
        setFormDefaults(defaults);
        setIsProfileLoading(false);
        setIsFormReady(true);
        return;
      }
      
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching profile:', error);
        }

        // Determine host name from profile, user metadata, or email
        const determinedHostName = profile?.display_name || 
                                  user.user_metadata?.display_name || 
                                  user.email?.split('@')[0] || '';

        console.log('🔥 DETERMINED HOST NAME:', determinedHostName);
        
        setUserProfile(profile);
        
        // Set form defaults with the correct host name
        const defaults = {
          name: '',
          description: '',
          hostName: determinedHostName,
          deadline: undefined,
          eventType: 'voting' as const,
          rsvpDate: '',
          rsvpTime: '',
          rsvpLocation: '',
        };
        
        setFormDefaults(defaults);
        setIsProfileLoading(false);
        setIsFormReady(true);
        
      } catch (error) {
        console.error('Error loading user profile:', error);
        const fallbackName = user.user_metadata?.display_name || user.email?.split('@')[0] || '';
        
        const defaults = {
          name: '',
          description: '',
          hostName: fallbackName,
          deadline: undefined,
          eventType: 'voting' as const,
          rsvpDate: '',
          rsvpTime: '',
          rsvpLocation: '',
        };
        
        setFormDefaults(defaults);
        setIsProfileLoading(false);
        setIsFormReady(true);
      }
    };

    if (user) {
      loadUserProfile();
    }
  }, [user?.id, user?.email, user?.user_metadata?.display_name]);

  // Reset form when defaults are ready
  useEffect(() => {
    if (isFormReady && formDefaults.hostName !== undefined) {
      console.log('🔄 RESETTING FORM WITH DEFAULTS:', formDefaults);
      form.reset(formDefaults);
      setFormInitialized(true);
    }
  }, [isFormReady, formDefaults, form]);

  // Handle template data from navigation
  useEffect(() => {
    if (templateData) {
      // Pre-fill form with template data
      form.setValue('name', templateData.name);
      form.setValue('description', templateData.description || templateData.defaultDescription);
      
      // Convert template options to our format
      // Handle both formats: array of {type, value} or array of {type, values[]}
      const convertedOptions = templateData.options.flatMap((option: any, index: number) => {
        if (option.values && Array.isArray(option.values)) {
          // Format from EventTemplateSelector
          return option.values.map((value: string, valueIndex: number) => ({
            id: `${index}-${valueIndex}`,
            type: option.type,
            value: value,
            votes: 0,
            voters: [],
          }));
        } else {
          // Simple format
          return {
            id: index.toString(),
            type: option.type,
            value: option.value,
            votes: 0,
            voters: [],
          };
        }
      });
      
      setOptions(convertedOptions);
      
      // Add suggested guests if available
      if (templateData.suggestedGuests) {
        const suggestedGuestObjects = templateData.suggestedGuests.map((email: string) => ({
          name: email.split('@')[0],
          email: email,
          phone: ''
        }));
        setGuests(suggestedGuestObjects);
      }
      
      // Skip template selection and go directly to details
      setCurrentStep('details');
    } else if (familyGroupId) {
      // If coming from family dashboard, skip template and go to details
      setCurrentStep('details');
    }
  }, [templateData, familyGroupId, form]);

  const handleTemplateSelect = (template: any) => {
    setSelectedTemplate(template);
    
    // Pre-fill form with template data
    form.setValue('name', template.name);
    form.setValue('description', template.description);
    
    // Convert template options to our format
    const convertedOptions = template.options.flatMap((option: any) => 
      option.values.map((value: string) => ({
        type: option.type,
        value: value
      }))
    );
    
    setOptions(convertedOptions);
    
    // Add suggested guests if available
    if (template.suggestedGuests) {
      const suggestedGuestObjects = template.suggestedGuests.map((email: string) => ({
        name: email.split('@')[0],
        email: email,
        phone: ''
      }));
      setGuests(suggestedGuestObjects);
    }
    
    setCurrentStep('details');
  };

  const addOption = () => {
    if (!newOption.value.trim()) return;
    
    // Validate option data
    try {
      const validatedData = optionSchema.parse(newOption);
      const newEventOption: EventOption = {
        id: Date.now().toString(),
        type: validatedData.type,
        value: validatedData.value,
        votes: 0,
        voters: [],
      };
      setOptions([...options, newEventOption]);
      setNewOption({ ...newOption, value: '' });
    } catch (error) {
      toast({
        title: 'Invalid option data',
        description: 'Please check the option information and try again.',
        variant: 'destructive',
      });
    }
  };

  const removeOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const addGuest = () => {
    if (!newGuest.email && !newGuest.phone && !newGuest.name) return;
    
    // Validate guest data
    try {
      const validatedGuest = guestSchema.parse(newGuest);
      setGuests([...guests, validatedGuest]);
      setNewGuest({});
    } catch (error) {
      toast({
        title: 'Invalid guest data',
        description: 'Please check the guest information and try again.',
        variant: 'destructive',
      });
    }
  };

  const removeGuest = (index: number) => {
    setGuests(guests.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: FormData) => {
    if (isSubmitting) {
      console.log('Form is already submitting, ignoring duplicate submission');
      return;
    }

    if (!user || !session) {
      toast({
        title: 'Authentication required',
        description: 'Please log in to create an event.',
        variant: 'destructive',
      });
      return;
    }

    console.log('Starting form submission for user:', user.email);
    setIsSubmitting(true);

    try {
      // 🔐 BULLETPROOF AUTHENTICATION VALIDATION
      console.log('🚀 Starting bulletproof authentication validation...');
      await debugAuthState();
      
      const dbAuthResult = await validateDatabaseAuth();
      if (!dbAuthResult.isValid) {
        console.error('❌ Database auth validation failed:', dbAuthResult);
        toast({
          title: 'Authentication Error',
          description: `Database authentication failed: ${dbAuthResult.error || 'Unknown error'}`,
          variant: 'destructive',
        });
        return;
      }
      
      console.log('✅ Database authentication validated successfully');

      // Check subscription limits
      const canCreateEvent = await checkCanCreateEvent();
      if (!canCreateEvent.canCreate) {
        toast({
          title: 'Event creation limit reached',
          description: canCreateEvent.reason,
          variant: 'destructive',
        });
        return;
      }

      // Check guest limits
      const canAddGuests = await checkCanAddGuests(guests.length);
      if (!canAddGuests.canAdd) {
        toast({
          title: 'Guest limit reached',
          description: canAddGuests.reason,
          variant: 'destructive',
        });
        return;
      }

      console.log('Authentication and limits verified. Creating event with data:', {
        name: data.name,
        description: data.description,
        hostName: data.hostName,
        eventType: data.eventType,
        guests: guests.length,
        options: options.length,
        userId: user.id,
        sessionUserId: session.user.id,
      });

      // 🛡️ BULLETPROOF EVENT CREATION WITH AUTH RETRY
      console.log('🛡️ Creating event with bulletproof authentication system...');
      const event = await createEvent({
        name: data.name,
        description: data.description || '',
        hostName: data.hostName,
        deadline: data.deadline?.toISOString(),
        eventType: data.eventType,
        options: options,
        guests: guests,
        rsvpDate: data.rsvpDate,
        rsvpTime: data.rsvpTime,
        rsvpLocation: data.rsvpLocation,
        allowGuestSuggestions: data.allowGuestSuggestions,
        clientId: selectedClientId,
      });

      console.log('Event created successfully:', {
        id: event.id,
        shareableLink: event.shareableLink,
        eventType: event.eventType,
      });
      
      // Send simple invitation emails
      await sendInvitationEmails(event);
      onEventCreated(event);
      
    } catch (error: any) {
      console.error('Error creating event for user:', user?.email, error);
      console.error('Full error object:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        sessionValid: session ? 'Yes' : 'No',
        userId: user?.id,
        sessionUserId: session?.user?.id,
      });
      
      // 🚨 ENHANCED ERROR HANDLING FOR BULLETPROOF AUTH
      if (error.message?.includes('Authentication') || error.message?.includes('session') || 
          error.message?.includes('auth') || error.message?.includes('policy') || 
          error.message?.includes('violates')) {
        toast({
          title: 'Security Error Detected',
          description: 'Authentication security error detected. Refreshing page to restore secure session...',
          variant: 'destructive',
        });
        
        console.log('🔄 Auth error detected, forcing secure page refresh...');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }
      
      // Show specific error message from the service
      const errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      
      toast({
        title: 'Failed to create event',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      console.log('Form submission completed for user:', user?.email);
      setIsSubmitting(false);
    }
  };

  const sendInvitationEmails = async (event: Event) => {
    // Send invitation emails if we have guest emails
    const guestsWithEmails = guests.filter(g => g.email);
    if (guestsWithEmails.length > 0) {
      try {
        // Get current session for authentication
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('No active session');
        }

        for (const guest of guestsWithEmails) {
          console.log('=== DEBUGGING SHAREABLE LINK ===');
          console.log('Event ID:', event.id);
          console.log('Event shareable link from DB:', event.shareableLink);
          console.log('Guest email:', guest.email);
          console.log('Event type:', event.eventType);
          console.log('Link length:', event.shareableLink?.length);
          console.log('Link format check:', /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(event.shareableLink || ''));
          
          // Use Supabase client instead of direct fetch
          const { error } = await supabase.functions.invoke('send-invitation-email', {
            body: {
              event: {
                id: event.id,
                name: event.name,
                host_name: event.hostName,
                event_type: event.eventType,
                deadline: event.deadline,
                shareable_link: event.shareableLink,
              },
              guest: {
                name: guest.name,
                email: guest.email,
              },
              shareableLink: event.shareableLink,
            },
          });

          if (error) {
            console.error('Error sending invitation email:', error);
            throw error;
          }
        }
        
        toast({
          title: 'Event created and invitations sent!',
          description: `Successfully created event and sent ${guestsWithEmails.length} invitation emails.`,
        });
      } catch (emailError) {
        console.error('Error sending emails:', emailError);
        toast({
          title: 'Event created',
          description: 'Event was created but some invitation emails may not have been sent.',
        });
      }
    } else {
      toast({
        title: 'Event created successfully!',
        description: 'You can now generate AI invitation images from your dashboard.',
      });
    }
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 'template':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Choose a Template</h2>
              <p className="text-muted-foreground mb-6">
                Start with a template or create from scratch
              </p>
            </div>
            
            <EventTemplates 
              onSelectTemplate={handleTemplateSelect}
              onClose={() => setCurrentStep('details')}
            />
            
            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
              <Button type="button" onClick={() => setCurrentStep('details')}>
                Skip Templates
              </Button>
            </div>
          </div>
        );

      case 'details':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              {!familyGroupId && (
                <Button variant="ghost" size="sm" onClick={() => setCurrentStep('template')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Templates
                </Button>
              )}
              {familyGroupId && (
                <Button variant="ghost" size="sm" onClick={onBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Family Dashboard
                </Button>
              )}
              {selectedTemplate && (
                <Badge variant="secondary" className="flex items-center gap-2">
                  <Sparkles className="w-3 h-3" />
                  Using: {selectedTemplate.name}
                </Badge>
              )}
              {familyGroupId && familyGroupName && (
                <Badge variant="outline" className="flex items-center gap-2">
                  <Home className="w-3 h-3" />
                  Family: {familyGroupName}
                </Badge>
              )}
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Event Details
                </CardTitle>
                <CardDescription>
                  Set up the basic information for your event
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Event Name</Label>
                  <Input
                    id="name"
                    {...form.register('name')}
                    placeholder="Enter event name"
                  />
                  {form.formState.errors.name && (
                    <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    {...form.register('description')}
                    placeholder="Add event description"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hostName">Host Name</Label>
                  <Input
                    id="hostName"
                    {...form.register('hostName')}
                    placeholder="Your name"
                  />
                  {form.formState.errors.hostName && (
                    <p className="text-sm text-destructive">{form.formState.errors.hostName.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <Select onValueChange={(value) => form.setValue('eventType', value as 'voting' | 'rsvp')} value={form.watch('eventType')}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="voting">Voting Event</SelectItem>
                      <SelectItem value="rsvp">RSVP Event</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    {form.watch('eventType') === 'voting' 
                      ? 'Guests will vote on multiple options (dates, times, locations)'
                      : 'Guests will simply accept or decline your invitation'
                    }
                  </p>
                </div>

                {form.watch('eventType') === 'rsvp' && (
                  <div className="space-y-4 p-4 border rounded-lg">
                    <h3 className="font-semibold">Event Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !form.watch('rsvpDate') && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {form.watch('rsvpDate') && !isNaN(new Date(form.watch('rsvpDate')!).getTime()) ? format(new Date(form.watch('rsvpDate')!), "PPP") : "Pick a date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={form.watch('rsvpDate') ? new Date(form.watch('rsvpDate')!) : undefined}
                              onSelect={(date) => form.setValue('rsvpDate', date?.toISOString().split('T')[0] || '')}
                              disabled={(date) => date < new Date()}
                              initialFocus
                              className="pointer-events-auto"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="rsvpTime">Time</Label>
                        <Input
                          id="rsvpTime"
                          type="time"
                          {...form.register('rsvpTime')}
                          className="w-full"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="rsvpLocation">Location</Label>
                        <Input
                          id="rsvpLocation"
                          {...form.register('rsvpLocation')}
                          placeholder="e.g., Community Center"
                        />
                      </div>
                    </div>
                  </div>
                )}

                  <ClientSelector
                    selectedClientId={selectedClientId}
                    onClientChange={setSelectedClientId}
                    label="Client (Optional)"
                    placeholder="Select a client for this event"
                  />

                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">
                        Allow Guest Suggestions
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Let guests suggest alternative dates, times, or locations for this event
                      </p>
                    </div>
                    <Switch
                      checked={form.watch('allowGuestSuggestions') || false}
                      onCheckedChange={(checked) => form.setValue('allowGuestSuggestions', checked)}
                    />
                  </div>

                 <div className="space-y-2">
                   <Label>Voting Deadline (Optional)</Label>
                   <Popover>
                     <PopoverTrigger asChild>
                       <Button
                         variant="outline"
                         className={cn(
                           "w-full justify-start text-left font-normal",
                           !form.watch('deadline') && "text-muted-foreground"
                         )}
                       >
                         <CalendarIcon className="mr-2 h-4 w-4" />
                         {form.watch('deadline') ? format(form.watch('deadline')!, "PPP") : "Pick a date"}
                       </Button>
                     </PopoverTrigger>
                     <PopoverContent className="w-auto p-0" align="start">
                       <Calendar
                         mode="single"
                         selected={form.watch('deadline')}
                         onSelect={(date) => form.setValue('deadline', date)}
                         disabled={(date) => date < new Date()}
                         initialFocus
                         className="pointer-events-auto"
                       />
                     </PopoverContent>
                   </Popover>
                 </div>

                <Button 
                  className="w-full" 
                  onClick={() => setCurrentStep(form.watch('eventType') === 'rsvp' ? 'guests' : 'options')}
                  disabled={!form.watch('name') || !form.watch('hostName')}
                >
                  Next: {form.watch('eventType') === 'rsvp' ? 'Add Guests' : 'Add Voting Options'}
                </Button>
              </CardContent>
            </Card>
          </div>
        );

      case 'options':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <Button variant="ghost" size="sm" onClick={() => setCurrentStep('details')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Details
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Voting Options</CardTitle>
                <CardDescription>
                  Add the options that people will vote on
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Select value={newOption.type} onValueChange={(value: 'date' | 'time' | 'location') => setNewOption({...newOption, type: value})}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="time">Time</SelectItem>
                      <SelectItem value="location">Location</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {newOption.type === 'date' ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="flex-1 justify-start">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newOption.value && !isNaN(new Date(newOption.value).getTime()) ? format(new Date(newOption.value), "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={newOption.value ? new Date(newOption.value) : undefined}
                          onSelect={(date) => setNewOption({...newOption, value: date?.toISOString().split('T')[0] || ''})}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  ) : newOption.type === 'time' ? (
                    <Input
                      type="time"
                      value={newOption.value}
                      onChange={(e) => setNewOption({...newOption, value: e.target.value})}
                      className="flex-1"
                    />
                  ) : (
                    <Input
                      placeholder="Enter location"
                      value={newOption.value}
                      onChange={(e) => setNewOption({...newOption, value: e.target.value})}
                      className="flex-1"
                    />
                  )}
                  
                  <Button onClick={addOption} disabled={!newOption.value}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {options.length > 0 && (
                  <div className="space-y-2">
                    <Label>Added Options</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {options.map((option, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-2">
                            {option.type === 'date' && <CalendarIcon className="w-4 h-4 text-muted-foreground" />}
                            {option.type === 'time' && <Clock className="w-4 h-4 text-muted-foreground" />}
                            {option.type === 'location' && <MapPin className="w-4 h-4 text-muted-foreground" />}
                            <span className="capitalize text-sm font-medium">{option.type}:</span>
                            <span className="text-sm">
                              {option.type === 'date' && option.value && !isNaN(new Date(option.value).getTime()) ? format(new Date(option.value), "PPP") : 
                               option.type === 'date' && (!option.value || isNaN(new Date(option.value).getTime())) ? "Invalid date" :
                               option.type === 'time' && option.value ? new Date(`2000-01-01T${option.value}`).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                               option.value}
                            </span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removeOption(index)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button 
                  className="w-full" 
                  onClick={() => setCurrentStep('guests')}
                  disabled={options.length === 0}
                >
                  Next: Add Guests
                </Button>
              </CardContent>
            </Card>
          </div>
        );

      case 'guests':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <Button variant="ghost" size="sm" onClick={() => setCurrentStep(form.watch('eventType') === 'rsvp' ? 'details' : 'options')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to {form.watch('eventType') === 'rsvp' ? 'Details' : 'Options'}
              </Button>
            </div>

            <Tabs defaultValue={familyGroupId ? "family" : "saved"} className="space-y-4">
              <TabsList className="grid w-full grid-cols-4">
                {familyGroupId && <TabsTrigger value="family">Family</TabsTrigger>}
                <TabsTrigger value="saved">Saved Contacts</TabsTrigger>
                <TabsTrigger value="individual">Add Individual</TabsTrigger>
                <TabsTrigger value="bulk">Bulk Add</TabsTrigger>
              </TabsList>

              {familyGroupId && (
                <TabsContent value="family" className="space-y-4">
                  <FamilyMemberSelector
                    selectedGuests={guests}
                    onGuestsChange={setGuests}
                    familyGroupId={familyGroupId}
                  />
                </TabsContent>
              )}

              <TabsContent value="saved" className="space-y-4">
                <GuestContactSelector 
                  selectedGuests={guests.map(g => ({
                    id: crypto.randomUUID(),
                    name: g.name,
                    email: g.email,
                    phone: g.phone,
                    hasVoted: false,
                    invitedAt: new Date().toISOString(),
                  }))}
                  onGuestsChange={(updatedGuests) => {
                    setGuests(updatedGuests.map(guest => ({
                      name: guest.name,
                      email: guest.email,
                      phone: guest.phone,
                    })));
                  }}
                />
              </TabsContent>

              <TabsContent value="individual" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Invite Guests
                    </CardTitle>
                    <CardDescription>
                      Add people to invite to your event
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Input
                        placeholder="Email"
                        value={newGuest.email || ''}
                        onChange={(e) => setNewGuest({...newGuest, email: e.target.value})}
                      />
                      <Input
                        placeholder="Phone (optional)"
                        value={newGuest.phone || ''}
                        onChange={(e) => setNewGuest({...newGuest, phone: e.target.value})}
                      />
                      <div className="flex gap-2">
                        <Input
                          placeholder="Name (optional)"
                          value={newGuest.name || ''}
                          onChange={(e) => setNewGuest({...newGuest, name: e.target.value})}
                        />
                        <Button onClick={addGuest} disabled={!newGuest.email && !newGuest.phone && !newGuest.name}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                     {!subscription.subscribed && (
                       <div className="p-3 bg-muted/50 rounded-lg border border-border">
                         <div className="flex items-center justify-between text-sm">
                           <span>Monthly Usage:</span>
                           <div className="flex gap-4">
                             <span className={subscription.monthlyEvents >= 5 ? 'text-destructive' : 'text-muted-foreground'}>
                               Events: {subscription.monthlyEvents}/5
                             </span>
                             <span className={subscription.monthlyGuests + guests.length > 20 ? 'text-destructive' : 'text-muted-foreground'}>
                               Guests: {subscription.monthlyGuests + guests.length}/20
                             </span>
                           </div>
                         </div>
                         {(subscription.monthlyEvents >= 4 || subscription.monthlyGuests + guests.length > 15) && (
                           <p className="text-xs text-muted-foreground mt-2">
                             Upgrade to Pro for unlimited events and guests
                           </p>
                         )}
                       </div>
                     )}

                     {guests.length > 0 && (
                       <div className="space-y-2">
                         <Label>Invited Guests ({guests.length})</Label>
                         <div className="space-y-2 max-h-40 overflow-y-auto">
                           {guests.map((guest, index) => (
                             <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                               <div className="flex items-center gap-2">
                                 <span className="text-sm">
                                   {guest.email || guest.phone || guest.name}
                                   {guest.name && guest.email && guest.name !== guest.email && (
                                     <span className="text-muted-foreground ml-2">({guest.name})</span>
                                   )}
                                 </span>
                               </div>
                               <Button variant="ghost" size="sm" onClick={() => removeGuest(index)}>
                                 <X className="w-4 h-4" />
                               </Button>
                             </div>
                           ))}
                         </div>
                       </div>
                     )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="bulk" className="space-y-4">
                <BulkGuestManager guests={guests} onGuestsChange={setGuests} />
              </TabsContent>
            </Tabs>

            <Card>
              <CardContent className="pt-6 space-y-4">
                {/* Show option to skip guests if client is assigned */}
                {selectedClientId && guests.length === 0 && (
                  <div className="p-4 bg-muted/50 rounded-lg border">
                    <p className="text-sm text-muted-foreground mb-3">
                      A client has been assigned to this event. You can skip adding guests now and let the client manage guests later.
                    </p>
                    <Button 
                      variant="outline"
                      className="w-full" 
                      onClick={() => {
                        // Set empty guest to allow submission
                        form.handleSubmit(onSubmit)();
                      }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Creating Event...' : 'Skip Guests & Create Event'}
                    </Button>
                  </div>
                )}
                
                <Button 
                  className="w-full" 
                  onClick={() => {
                    console.log('=== CREATE EVENT BUTTON CLICKED ===');
                    console.log('Guests length:', guests.length);
                    console.log('Is submitting:', isSubmitting);
                    console.log('Client assigned:', selectedClientId);
                    
                    // Allow creation without guests if client is assigned
                    if (guests.length === 0 && !selectedClientId) {
                      console.log('Button disabled: No guests added and no client assigned');
                      toast({
                        title: 'No guests added',
                        description: 'Please add at least one guest to invite, or assign a client.',
                        variant: 'destructive',
                      });
                      return;
                    }
                    
                    form.handleSubmit(onSubmit)();
                  }}
                  disabled={(guests.length === 0 && !selectedClientId) || isSubmitting}
                >
                  {isSubmitting ? '🔐 Creating Event with Secure Auth...' : '🚀 Create Event'}
                </Button>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading state while profile is loading
  if (isProfileLoading || !isFormReady || !formInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-4">
        <div className="container mx-auto max-w-4xl">
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                <p className="text-muted-foreground">Loading your profile...</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-4">
      <div className="container mx-auto max-w-4xl">
        {getStepContent()}
      </div>
    </div>
  );
};